/**
 * Currency Analysis Page
 * NGN/USD exchange rate tracking and analysis (FR-300)
 * Supports time-aligned comparison between FX, global, and local prices
 */

import React, { useState, useMemo, useEffect } from 'react';
import PlanBanner from '../../components/PlanBanner';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart,
  Legend,
  ReferenceLine
} from 'recharts';
import { PageHeader, MetricCard, ChartCard, getChartStyles, LoadingSkeleton, EmptyState, DeltaChip, RangeLockWrap, SourceFreshness } from '../../components/common';
import { useFxSeries, useFxLatest, useGlobalPrices, useGlobalCommodities, useWorldSeriesCatalogue, useWorldReferenceBatch } from '../../services/queries';
import { FX_SERIES, FX_LONG_RANGES, showsPegEra as pegEraVisible, thinForPlot, withRangeAvailability } from '../../utils/fxSeries';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  WORLD_MONTHS, WORLD_UNITS, DEFAULT_WORLD_SERIES,
  toPriceMap, toSeriesMap, toPickerOptions, exchangeFeedIsEmpty,
  unitLabelFor, kgPerPublishedUnit,
} from '../../utils/worldReference';
import { getPrefs } from '../../utils/preferences';
import { useRecipes } from '../../theme/recipes';
import { ExportButton } from '../../components/common';


// Shared monospace font for numeric values
const firaCode = { fontFamily: MONO };

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '-' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Time range options
// Axis date label: day + month inside ~6 months of span, month + year beyond.
const fmtAxisDate = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
// Day spans, cut by date (the CBN feed is daily; a point-count slice would
// misrepresent the period).
const TIME_RANGES = [
  { label: '1M', value: 31 },
  { label: '3M', value: 92 },
  { label: '6M', value: 183 },
  { label: '1Y', value: 366 }
];
// FX series definitions live in utils/fxSeries so this page and the public
// Insights page describe them identically.
const cutRangeByDate = (rows, days) => {
  if (days >= 99999) return rows || [];
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const iso = cutoff.toISOString().slice(0, 10);
  return (rows || []).filter((r) => r.date >= iso);
};

function CurrencyAnalysis() {
  const { user: authUser } = useAuth();
  // Default chart window comes from the user's Settings preference.
  const [timeRange, setTimeRange] = useState(
    () => ({ '1M': 31, '3M': 92, '6M': 183 }[getPrefs(authUser?.email).chartRange] || 183)
  );
  const [fxAdjustedCommodityId, setFxAdjustedCommodityId] = useState('GC002');
  const [fxSeriesKey, setFxSeriesKey] = useState('nfem');
  const fxSeries = FX_SERIES[fxSeriesKey];
  const { isDark, palette } = useThemeMode();
  const chartStyles = getChartStyles(isDark);
  const recipes = useRecipes();

  const { data: fxRates = [], isLoading: fxLoading } = useFxSeries('ALL', fxSeries.pair);
  const { data: fxLatest } = useFxLatest(fxSeries.pair);
  const { data: globalCommodities = [], isLoading: gcLoading } = useGlobalCommodities();
  const { data: exchangePrices = {}, isLoading: gpLoading } = useGlobalPrices('ALL');
  const loading = fxLoading || gcLoading || gpLoading;

  // Same fallback as the commodities pages: the ICE and CBOT feed expired, so
  // in production this comparison had no prices at all and rendered an empty
  // chart under a "Source: CBN x CBOT" byline. The World Bank series fills it,
  // and the labelling has to change with it.
  const usingWorldReference = exchangeFeedIsEmpty(exchangePrices);
  const { data: worldCatalogue } = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = useMemo(() => toPickerOptions(worldCatalogue), [worldCatalogue]);
  const allSeriesIds = useMemo(() => worldOptions.map((o) => o.id), [worldOptions]);
  const { data: worldBatch } = useWorldReferenceBatch(
    allSeriesIds, WORLD_MONTHS, { enabled: usingWorldReference && allSeriesIds.length > 0 },
  );
  const worldPrices = useMemo(() => toPriceMap(worldBatch), [worldBatch]);
  const worldSeries = useMemo(() => toSeriesMap(worldBatch), [worldBatch]);
  const globalCommodityPrices = usingWorldReference ? worldPrices : exchangePrices;

  // Weight unit for world figures, tonne by default, for the same reason as
  // the commodities pages: the Pink Sheet quotes cocoa per kilo.
  const [worldUnit, setWorldUnit] = useState('MT');

  // In world mode the choices are series, so the selection lives in that id
  // space and the "commodity" is a synthetic row built from the series.
  const fxAdjustedCommodity = useMemo(() => {
    if (usingWorldReference) {
      const o = worldOptions.find((x) => x.id === fxAdjustedCommodityId) || worldOptions[0];
      return o ? { id: o.id, name: o.label, unit: o.unit, exchange: 'World Bank Pink Sheet' } : undefined;
    }
    return globalCommodities.find((c) => c.id === fxAdjustedCommodityId) || globalCommodities[0];
  }, [usingWorldReference, worldOptions, fxAdjustedCommodityId, globalCommodities]);

  const fxAdjustedUnitSuffix = useMemo(() => {
    if (usingWorldReference) {
      const chosen = WORLD_UNITS.find((u) => u.value === worldUnit);
      return chosen ? chosen.suffix.replace('/', '') : '';
    }
    const unit = fxAdjustedCommodity?.unit || 'USD';
    const parts = unit.split('/');
    return parts[1] ? parts[1].toLowerCase() : '';
  }, [usingWorldReference, worldUnit, fxAdjustedCommodity]);

  // A selection carried over from the exchange id space resolves to nothing.
  useEffect(() => {
    if (!usingWorldReference || allSeriesIds.length === 0) return;
    if (!allSeriesIds.includes(fxAdjustedCommodityId)) {
      setFxAdjustedCommodityId(
        allSeriesIds.includes(DEFAULT_WORLD_SERIES) ? DEFAULT_WORLD_SERIES : allSeriesIds[0],
      );
    }
  }, [usingWorldReference, allSeriesIds, fxAdjustedCommodityId]);

  // Long ranges exist only for the official archive; switching back to NFEM
  // snaps an out-of-reach window down so the chart never looks empty.
  const offeredRanges = useMemo(
    () => withRangeAvailability(fxRates, [...TIME_RANGES, ...FX_LONG_RANGES]),
    [fxRates]
  );
  const handleSeriesChange = (key) => setFxSeriesKey(key);

  // Switching series can leave the selection on a window the new series cannot
  // fill. Fall back to the widest one it can, so the chart is never labelled
  // with a span it does not actually cover.
  useEffect(() => {
    if (offeredRanges.find((r) => r.value === timeRange)?.disabled) {
      const fallback = [...offeredRanges].reverse().find((r) => !r.disabled);
      if (fallback) setTimeRange(fallback.value);
    }
  }, [offeredRanges, timeRange]);

  // Filter FX data by time range, and drop rows carrying no rate.
  //
  // `rate` is nullable. Everything downstream here assumes it is a number:
  // the statistics take Math.max/min over the column, where a null reads as
  // 0 and flattens the low to zero; the correlation series multiplies by it,
  // where `close * null` is 0 and plots a real ₦0; and the tooltip formats
  // it, where null throws. One filter here is the whole fix.
  const filteredFXData = useMemo(() => {
    return cutRangeByDate(fxRates, timeRange).filter((d) => d.rate != null);
  }, [timeRange, fxRates]);

  // Only warn about the peg when the visible window actually reaches into it.
  const showsPegEra = pegEraVisible(fxSeriesKey, filteredFXData);

  // Calculate statistics
  const stats = useMemo(() => {
    if (filteredFXData.length < 2) return null;

    const rates = filteredFXData.map(d => d.rate);
    const latest = rates[rates.length - 1];
    const previous = rates[rates.length - 2];
    const first = rates[0];
    const high = Math.max(...rates);
    const low = Math.min(...rates);
    const avg = rates.reduce((a, b) => a + b, 0) / rates.length;

    return {
      current: latest,
      previous,
      periodStart: first,
      dayChange: latest - previous,
      dayChangePercent: ((latest - previous) / previous * 100).toFixed(2),
      periodChange: latest - first,
      periodChangePercent: ((latest - first) / first * 100).toFixed(2),
      high,
      low,
      avg: Math.round(avg),
      volatility: ((high - low) / avg * 100).toFixed(2)
    };
  }, [filteredFXData]);

  // Chart data with additional metrics
  const chartData = useMemo(() => {
    // Thinned for drawing only; the stat cards still read the full series.
    const plotted = thinForPlot(filteredFXData);
    return plotted.map((item, index) => {
      const prevRate = index > 0 ? plotted[index - 1].rate : item.rate;
      const change = item.rate - prevRate;
      const changePercent = (change / prevRate * 100).toFixed(2);

      return {
        date: item.date,
        fullDate: item.date,
        rate: item.rate,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
        change,
        changePercent: parseFloat(changePercent)
      };
    });
  }, [filteredFXData]);

  // FX-adjusted comparison data for the selected global commodity.
  // Joined BY DATE: the two series have different lengths and holidays, so an
  // index join would pair prices from different days.
  const correlationData = useMemo(() => {
    const rows = globalCommodityPrices[fxAdjustedCommodityId] || [];
    // A monthly world price and a daily FX rate cannot be joined on the exact
    // day: they would meet only on the first of each month and the chart
    // would be twelve lonely points a year. A monthly average applies to its
    // whole month, so every day in August carries August's figure. The USD
    // line then steps monthly while the NGN line moves daily, which is the
    // comparison worth drawing: the world price held, the naira moved, so
    // the cost in naira changed.
    const key = (d) => (usingWorldReference ? String(d).slice(0, 7) : d);
    const closeBy = new Map(rows.map((p) => [key(p.date), p.close]));
    // Converted to the chosen weight, so the axis and the unit label agree.
    const kgPerNative = kgPerPublishedUnit(worldSeries[fxAdjustedCommodityId]?.unit);
    const toDisplay = (v) => {
      if (v == null) return null;
      if (!usingWorldReference || !kgPerNative) return v;
      const perKg = v / kgPerNative;
      if (worldUnit === 'kg') return perKg;
      if (worldUnit === 'lb') return perKg * 0.453592;
      return perKg * 1000;
    };
    return filteredFXData.map((fx) => {
      const close = toDisplay(closeBy.get(key(fx.date)));
      const ngn = close != null && fx.rate != null ? close * fx.rate : null;
      return {
        date: fx.date,
        fxRate: fx.rate,
        usdPrice: close ?? null,
        ngnPrice: ngn != null ? Math.round(ngn) : null
      };
    });
  }, [filteredFXData, fxAdjustedCommodityId, globalCommodityPrices, usingWorldReference, worldSeries, worldUnit]);

  // Rising NGN/USD = weaker naira = caution (amber-red side); falling = good.
  const getTrendIcon = (value) => {
    if (value > 0) return <Icon icon="solar:arrow-up-outline" width={20} height={20} color={palette.danger} />;
    if (value < 0) return <Icon icon="solar:arrow-down-outline" width={20} height={20} color={palette.accent} />;
    return <Icon icon="solar:minus-square-outline" width={20} height={20} color={palette.textMuted} />;
  };

  if (loading) {
    return (
      <Box>
        <PageHeader
          eyebrow="Analytics"
          title="Currency Analysis"
          subtitle="NGN/USD exchange rate tracking and FX-adjusted price analysis"
          breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Currency Analysis' }]}
        />
        <LoadingSkeleton variant="page" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Analytics"
        title="Currency Analysis"
        subtitle="NGN/USD exchange rate tracking and FX-adjusted price analysis"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Currency Analysis' }
        ]}
        actions={
          <ExportButton
            filename="ngn-usd-rates"
            label="Export data"
            headers={['Date', 'Rate', 'Open', 'High', 'Low', 'Close']}
            rows={chartData.map((r) => [r.date, r.rate, r.open ?? '', r.high ?? '', r.low ?? '', r.close ?? ''])}
          />
        }
      />

      {/* Key Metrics - a rising NGN/USD rate reads as caution, never a win,
          so the change card uses invertTrend and High/Low stay neutral. */}
      <PlanBanner adds={['FX-adjusted comparisons', 'export', 'unlimited alerts and holdings']} />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            title="Current Rate"
            value={stats ? `₦${stats.current.toLocaleString()}` : '-'}
            subtitle={`NGN per USD · ${fxSeries.label}`}
            caption={`Updated · ${fmtDate(fxRates[fxRates.length - 1]?.date)}`}
            icon={<Icon icon="solar:dollar-minimalistic-outline" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            title="Daily Change"
            value={stats ? `${stats.dayChange >= 0 ? '+' : '-'}₦${Math.abs(stats.dayChange).toFixed(0)}` : '-'}
            subtitle="vs previous close"
            caption={`Updated · ${fmtDate(fxRates[fxRates.length - 1]?.date)}`}
            icon={<Icon icon={stats?.dayChange >= 0 ? 'solar:arrow-up-outline' : 'solar:arrow-down-outline'} width={20} height={20} />}
            trend={stats ? (stats.dayChange >= 0 ? 'up' : 'down') : undefined}
            trendValue={stats ? `${stats.dayChangePercent}%` : undefined}
            invertTrend
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            title="Period High"
            value={stats ? `₦${stats.high.toLocaleString()}` : '-'}
            subtitle="weakest naira in range"
            caption={`Updated · ${fmtDate(fxRates[fxRates.length - 1]?.date)}`}
            icon={<Icon icon="solar:graph-up-outline" width={20} height={20} />}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <MetricCard
            title="Period Low"
            value={stats ? `₦${stats.low.toLocaleString()}` : '-'}
            subtitle="strongest naira in range"
            caption={`Updated · ${fmtDate(fxRates[fxRates.length - 1]?.date)}`}
            icon={<Icon icon="solar:graph-down-outline" width={20} height={20} />}
          />
        </Grid>
      </Grid>

      {/* Main FX Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            role="group"
            aria-label="Exchange rate series"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              flexWrap: 'wrap', mb: 1.5
            }}
          >
            <Typography
              sx={{
                fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: palette.textMuted, mr: 0.25
              }}
            >
              Rate series
            </Typography>
            {Object.entries(FX_SERIES).map(([key, cfg]) => (
              <Button
                key={key}
                size="small"
                variant="outlined"
                onClick={() => handleSeriesChange(key)}
                aria-pressed={fxSeriesKey === key}
                sx={recipes.rangeBtnSx(fxSeriesKey === key)}
              >
                {cfg.label}
              </Button>
            ))}
          </Box>
          {showsPegEra && (
            <Box
              role="note"
              sx={{
                mb: 1.5, px: 1.75, py: 1.25, borderRadius: '12px',
                bgcolor: palette.warningBg, border: `1px solid ${palette.warning}33`
              }}
            >
              <Typography sx={{ fontSize: '0.78rem', color: palette.text, lineHeight: 1.5 }}>
                <strong>Before June 2023 this was an administered rate.</strong> It held flat
                for years at levels most buyers could not actually obtain, while the parallel
                market traded far higher. Read the early years as policy, not as market prices.
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
          <ChartCard
            title="NGN/USD Exchange Rate"
            subtitle={
              <>
                {fxSeries.blurb}
                <Typography component="span" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', fontSize: '0.72rem', mt: 0.25 }}>
                  {fxSeries.source}
                </Typography>
                <SourceFreshness
                  component="span"
                  date={fxLatest?.date}
                  checkedAt={fxLatest?.checkedAt}
                  sx={{ display: 'block', mt: 0.25 }}
                />
              </>
            }
            height={350}
            actions={
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {offeredRanges.map((range) => (
                  <RangeLockWrap key={range.label} days={range.value}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setTimeRange(range.value)}
                      disabled={range.disabled}
                      title={range.disabled ? `${fxSeries.label} does not go back that far` : undefined}
                      sx={recipes.rangeBtnSx(timeRange === range.value)}
                    >
                      {range.label}
                    </Button>
                  </RangeLockWrap>
                ))}
              </Box>
            }
          >
            {(chartData && chartData.length > 0) ? (
            <ResponsiveContainer width="100%" height={310}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="fxGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22C55E" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...chartStyles.grid} />
                <XAxis dataKey="date" {...chartStyles.axis} minTickGap={26} tickFormatter={fmtAxisDate} />
                <YAxis
                  domain={['dataMin - 30', 'dataMax + 30']}
                  {...chartStyles.axis}
                  tickFormatter={(v) => `₦${v}`}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      // Plain style, NOT sx: the recipe's numeric borderRadius
                      // means pixels; sx would scale it by the theme (10 -> 100px oval).
                      <Box style={chartStyles.tooltip.contentStyle}>
                        <Typography variant="body2" fontWeight={600}>{fmtDate(data.fullDate)}</Typography>
                        <Typography variant="body2" sx={firaCode}>Rate: {data.rate != null ? `₦${data.rate.toLocaleString()}` : '-'}</Typography>
                        <Typography variant="body2" sx={firaCode}>High: {data.high != null ? `₦${data.high.toLocaleString()}` : '-'}</Typography>
                        <Typography variant="body2" sx={firaCode}>Low: {data.low != null ? `₦${data.low.toLocaleString()}` : '-'}</Typography>
                        <Typography
                          variant="body2"
                          color={data.change >= 0 ? 'error.main' : 'success.main'}
                          sx={firaCode}
                        >
                          Change: {data.change >= 0 ? '+' : ''}{data.changePercent}%
                        </Typography>
                      </Box>
                    );
                  }}
                />
                <ReferenceLine
                  y={stats?.avg}
                  stroke={palette.textMuted}
                  strokeDasharray="5 5"
                  label={{ value: `Avg ₦${stats?.avg?.toLocaleString()}`, position: 'insideTopRight', fill: palette.textMuted, fontSize: 11, fontFamily: MONO }}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke="#22C55E"
                  strokeWidth={2}
                  fill="url(#fxGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
            ) : (
              <EmptyState
                compact
                title="No FX data yet"
                description="Exchange-rate history will appear here once published."
                sx={{ height: 310 }}
              />
            )}
            {stats && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Dashed line indicates period average (₦{stats.avg.toLocaleString()})
                </Typography>
              </Box>
            )}
          </ChartCard>
          </Box>
        </Grid>

        {/* Statistics Panel */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Rate Statistics"
              titleTypographyProps={{ variant: 'h6' }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Period Change</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getTrendIcon(stats?.periodChange)}
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      color={stats?.periodChange >= 0 ? 'error.main' : 'success.main'}
                      sx={firaCode}
                    >
                      {stats ? `${stats.periodChange >= 0 ? '+' : '-'}₦${Math.abs(stats.periodChange).toFixed(0)}` : '-'}
                      <Typography component="span" variant="body2" sx={{ ml: 1, ...firaCode }}>
                        {stats ? `(${stats.periodChangePercent}%)` : ''}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary">Volatility</Typography>
                  <Typography variant="h6" fontWeight={700} sx={firaCode}>
                    {stats ? `${stats.volatility}%` : '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (High-Low range relative to average)
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="body2" color="text.secondary">Period Average</Typography>
                  <Typography variant="h6" fontWeight={700} sx={firaCode}>{stats ? `₦${stats.avg.toLocaleString()}` : '-'}</Typography>
                </Box>

                <Divider />

                <Box sx={{ mt: 1, p: 1.75, borderRadius: '10px', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Icon icon="ph:info" width={16} height={16} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary, lineHeight: 1.55 }}>
                    NGN depreciation (rate increase) typically raises local commodity
                    prices for imported goods and enhances export competitiveness.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* FX-Adjusted Global Price */}
        <Grid item xs={12}>
          <ChartCard
            title="FX-Adjusted Commodity Comparison"
            subtitle={
              <>
                {fxAdjustedCommodity?.name} price in USD vs NGN equivalent
                <Typography component="span" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', fontSize: '0.72rem', mt: 0.25 }}>
                  Source: CBN × {fxAdjustedCommodity?.exchange}
                  {usingWorldReference && ' · monthly world average against the daily rate'}
                </Typography>
              </>
            }
            actions={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {/* Same weight unit control as the commodities pages, so the
                  axis, the tooltip and the label all agree on what a tonne
                  is. Only meaningful for world data, which is published per
                  kilo or per tonne rather than per bushel. */}
              {usingWorldReference && (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="fx-adjusted-unit-label">Unit</InputLabel>
                  <Select
                    labelId="fx-adjusted-unit-label"
                    label="Unit"
                    value={worldUnit}
                    onChange={(e) => setWorldUnit(e.target.value)}
                  >
                    {WORLD_UNITS.map((u) => (
                      <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="fx-adjusted-commodity-label">Commodity</InputLabel>
                <Select
                  labelId="fx-adjusted-commodity-label"
                  label="Commodity"
                  value={fxAdjustedCommodityId}
                  onChange={(e) => setFxAdjustedCommodityId(e.target.value)}
                >
                  {(usingWorldReference
                    ? worldOptions.map((o) => ({ id: o.id, name: o.label, unit: unitLabelFor(o.unit, 'USD', worldUnit) }))
                    : globalCommodities
                  ).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.unit})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              </Box>
            }
            height={350}
          >
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={correlationData}>
                <CartesianGrid {...chartStyles.grid} />
                <XAxis dataKey="date" {...chartStyles.axis} minTickGap={26} tickFormatter={fmtAxisDate} />
                <YAxis
                  yAxisId="left"
                  {...chartStyles.axis}
                  tickFormatter={(v) => `$${typeof v === 'number' ? (v < 100 ? v.toFixed(2) : v.toFixed(0)) : v}`}
                  domain={['auto', 'auto']}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  {...chartStyles.axis}
                  tickFormatter={(v) => `₦${(v / 1000).toFixed(1)}k`}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0]?.payload;
                    if (!data) return null;
                    return (
                      <Box style={chartStyles.tooltip.contentStyle}>
                        <Typography variant="body2" fontWeight={600}>{fxAdjustedCommodity?.name}</Typography>
                        <Typography variant="body2" sx={firaCode}>
                          USD Price: ${data.usdPrice?.toFixed(2)}{fxAdjustedUnitSuffix ? `/${fxAdjustedUnitSuffix}` : ''}
                        </Typography>
                        <Typography variant="body2" sx={firaCode}>NGN Equivalent: ₦{data.ngnPrice?.toLocaleString()}</Typography>
                        <Typography variant="body2" sx={firaCode}>FX Rate: ₦{data.fxRate?.toLocaleString()}</Typography>
                      </Box>
                    );
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="usdPrice"
                  stroke="#22C55E"
                  strokeWidth={2}
                  name={`${fxAdjustedCommodity?.name} (USD)`}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="ngnPrice"
                  stroke="#1E4D80"
                  strokeWidth={2}
                  name={`${fxAdjustedCommodity?.name} (NGN)`}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Historical Data Table */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Historical Exchange Rates"
              titleTypographyProps={{ variant: 'h6' }}
              action={
                <ExportButton
                  filename="ngn-usd-history"
                  label="Export"
                  headers={['Date', 'Rate', 'Open', 'High', 'Low', 'Close', 'Change %']}
                  rows={chartData.map((r) => [r.fullDate, r.rate, r.open ?? '', r.high ?? '', r.low ?? '', r.close ?? '', r.changePercent])}
                />
              }
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Open', 'High', 'Low', 'Close', 'Change'].map((col, i) => (
                        <TableCell key={col} align={i === 0 ? 'left' : 'right'} sx={recipes.headCellSx}>
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {chartData.slice().reverse().map((row) => (
                      <TableRow key={row.fullDate} hover sx={recipes.bodyRowSx}>
                        <TableCell sx={{ ...firaCode, color: palette.textMuted, fontSize: '0.8rem' }}>{fmtDate(row.fullDate)}</TableCell>
                        {/* The daily CBN feed does not publish an opening rate, and
                            fallback-sourced rows may lack high/low - render '-' for
                            missing fields instead of crashing. */}
                        <TableCell align="right" sx={firaCode}>{row.open != null ? `₦${row.open.toLocaleString()}` : '-'}</TableCell>
                        <TableCell align="right" sx={firaCode}>{row.high != null ? `₦${row.high.toLocaleString()}` : '-'}</TableCell>
                        <TableCell align="right" sx={firaCode}>{row.low != null ? `₦${row.low.toLocaleString()}` : '-'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, ...firaCode }}>{row.close != null ? `₦${row.close.toLocaleString()}` : '-'}</TableCell>
                        <TableCell align="right">
                          <DeltaChip value={row.changePercent} caution={row.change > 0} decimals={2} size="sm" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CurrencyAnalysis;
